import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTenant } from "@/contexts/TenantContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { TrialBanner } from "./subscription/TrialBanner";
import { FABQuickActions } from "@/components/FABQuickActions";
import { useLocation, useNavigate } from "react-router-dom";
import { NotificationsBell } from "@/components/NotificationsBell";

export function Layout({ children }: { children: React.ReactNode }) {
  const { tenantName, isAdminSaas, isImpersonating, subscriptionPlanId, subscriptionStatus, getDaysUntilExpiration, stopImpersonation } = useTenant();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isPDVRoute = useMemo(() => location.pathname.startsWith("/pdv"), [location.pathname]);
  const search = new URLSearchParams(location.search);
  const embedded = search.get("embedded") === "1" || search.get("hideHeader") === "1";
  
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col w-full" data-cy="layout-container">
        {!embedded && (
          <header className="h-14 border-b bg-background flex items-center px-4 sticky top-0 z-10 gap-2" data-cy="main-header">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-cy="mobile-menu-toggle">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px]">
                <AppSidebar onNavigate={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2 flex-1 truncate" data-cy="header-title">
              {isImpersonating ? (
                <h1 className="text-base font-semibold truncate">{tenantName}</h1>
              ) : isAdminSaas ? (
                <h1 className="text-base font-semibold truncate">Admin SaaS</h1>
              ) : tenantName ? (
                <h1 className="text-base font-semibold truncate">{tenantName}</h1>
              ) : (
                <img 
                  src="/logo-fundo-branco.png" 
                  alt="Logo" 
                  className="h-8 w-auto object-contain"
                />
              )}
            </div>
            <ThemeToggle data-cy="theme-toggle" />
            <NotificationsBell />
          </header>
        )}
        {!embedded && isImpersonating && (
          <div className="w-full bg-amber-500 text-amber-50 px-4 py-2 text-sm flex items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <span className="font-semibold">Impersonate ativo</span>
              <span className="hidden sm:inline">Tenant: {tenantName}</span>
              <span className="hidden sm:inline">Plano: {subscriptionPlanId || 'Trial'}</span>
              <span>Status: {subscriptionStatus}</span>
              <span>
                {(() => {
                  const d = getDaysUntilExpiration();
                  return d === null ? 'Sem vencimento' : `Expira em ${d} dias`;
                })()}
              </span>
            </div>
            <Button variant="outline" size="sm" className="bg-white text-amber-700 hover:bg-amber-100" onClick={async () => { await stopImpersonation(); navigate('/clientes-saas'); }}>
              Sair do Impersonate
            </Button>
          </div>
        )}
        {!embedded && !isPDVRoute && <TrialBanner />}
        <main className={`flex-1 ${embedded ? 'p-0' : 'p-4'} bg-background overflow-x-hidden`} data-cy="main-content">
          {children}
        </main>
        {!embedded && <FABQuickActions />}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full" data-cy="layout-container">
        {!embedded && <AppSidebar />}
        <div className="flex-1 flex flex-col">
          {!embedded && (
            <header className="h-14 border-b bg-background flex items-center px-4 sticky top-0 z-10 gap-4" data-cy="main-header">
              <SidebarTrigger data-cy="sidebar-toggle" />
              <div className="flex items-center gap-2 flex-1" data-cy="header-title">
                {isImpersonating ? (
                  <h1 className="text-lg font-semibold flex-1">{tenantName}</h1>
                ) : isAdminSaas ? (
                  <h1 className="text-lg font-semibold flex-1">Admin SaaS</h1>
                ) : tenantName ? (
                  <h1 className="text-lg font-semibold flex-1">{tenantName}</h1>
                ) : (
                  <img 
                    src="/logo-fundo-branco.png" 
                    alt="Logo" 
                    className="h-8 w-auto object-contain"
                  />
                )}
              </div>
              <ThemeToggle data-cy="theme-toggle" />
              <NotificationsBell />
            </header>
          )}
          {!embedded && isImpersonating && (
            <div className="w-full bg-amber-500 text-amber-50 px-6 py-2 text-sm flex items-center gap-4 justify-between">
              <div className="flex items-center gap-4">
                <span className="font-semibold">Impersonate ativo</span>
                <span>Tenant: {tenantName}</span>
                <span>Plano: {subscriptionPlanId || 'Trial'}</span>
                <span>Status: {subscriptionStatus}</span>
                <span>
                  {(() => {
                    const d = getDaysUntilExpiration();
                    return d === null ? 'Sem vencimento' : `Expira em ${d} dias`;
                  })()}
                </span>
              </div>
              <Button variant="outline" size="sm" className="bg-white text-amber-700 hover:bg-amber-100" onClick={async () => { await stopImpersonation(); navigate('/clientes-saas'); }}>
                Sair do Impersonate
              </Button>
            </div>
          )}
          {!embedded && !isPDVRoute && <TrialBanner />}
          <main className={`flex-1 ${embedded ? 'p-0' : 'p-6'} bg-background`} data-cy="main-content">
            {children}
          </main>
          {!embedded && <FABQuickActions />}
        </div>
      </div>
    </SidebarProvider>
  );
}
